import type { Recipient } from '@/lib/types/recipients';

/**
 * Normalize raw API response to the frontend Recipient type.
 *
 * The backend may return:
 *   - `tags` as {id, name, color}[]  → we extract name strings
 *   - `groups` as {id, name, ...}[]  → we map to `lists` as ID strings
 *   - `customFieldValues`            → we rename to `customFields`
 *   - any of these as null/undefined → we default to []/{}
 *
 * RULE: Never use `?? []` or `?? {}` in components for API data.
 * Normalize here at the API layer.
 */
export function normalizeRecipient(raw: Record<string, unknown>): Recipient {
  const tags = Array.isArray(raw.tags)
    ? (raw.tags as Array<string | { name: string }>).map((t) =>
        typeof t === 'string' ? t : (t.name ?? ''),
      )
    : [];

  const lists = Array.isArray(raw.groups)
    ? (raw.groups as Array<string | { id: string }>).map((g) =>
        typeof g === 'string' ? g : (g.id ?? ''),
      )
    : Array.isArray(raw.lists)
    ? (raw.lists as string[])
    : [];

  const customFields =
    (raw.customFieldValues as Record<string, unknown>) ??
    (raw.customFields as Record<string, unknown>) ??
    {};

  return { ...(raw as unknown as Recipient), tags, lists, customFields } as Recipient;
}
